import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/LanguageContext';

/**
 * Visual for "Die unterschätzte Chance" slide.
 *
 * Editorial hero photograph: raw diamond vs. cut brilliant with a slow
 * Ken-Burns reveal. The former donut chart has been removed — the slide
 * now focuses purely on the photograph next to the copy.
 */
export default function ChanceVisual() {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const { lang } = useT();

  const CAPTION = {
    de: { raw: 'Rohdiamant',   brilliant: 'Brillant',       alt: 'Rohdiamant im Vergleich zum geschliffenen Brillanten' },
    en: { raw: 'Rough diamond', brilliant: 'Brilliant',      alt: 'Rough diamond compared to a cut brilliant' },
    tr: { raw: 'Ham Elmas',    brilliant: 'Pırlanta',        alt: 'Ham elmas ile kesilmiş pırlanta karşılaştırması' },
    hu: { raw: 'Nyers gyémánt', brilliant: 'Briliáns',       alt: 'Nyers gyémánt és csiszolt briliáns összehasonlítása' },
    fr: { raw: 'Diamant brut', brilliant: 'Brillant',        alt: 'Diamant brut comparé à un brillant taillé' },
    es: { raw: 'Diamante en bruto', brilliant: 'Brillante',  alt: 'Diamante en bruto comparado con un brillante tallado' },
    it: { raw: 'Diamante grezzo', brilliant: 'Brillante',    alt: 'Diamante grezzo a confronto con un brillante tagliato' },
    pt: { raw: 'Diamante em bruto', brilliant: 'Brilhante',  alt: 'Diamante em bruto comparado com um brilhante lapidado' },
    nl: { raw: 'Ruwe diamant', brilliant: 'Briljant',        alt: 'Ruwe diamant vergeleken met een geslepen briljant' },
    ru: { raw: 'Необработанный алмаз', brilliant: 'Бриллиант', alt: 'Необработанный алмаз в сравнении с бриллиантом' },
    pl: { raw: 'Surowy diament', brilliant: 'Brylant',       alt: 'Surowy diament w porównaniu ze szlifowanym brylantem' },
  };
  const c = CAPTION[lang] || CAPTION.en;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: '-10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className={`chance-visual chance-visual--photo-only ${visible ? 'is-visible' : ''}`}
      data-testid="chance-visual"
    >
      <div className="chance-photo-frame">
        <img
          src="/images/chance-diamonds.jpeg"
          alt={c.alt}
          className="chance-photo"
          loading="lazy"
          draggable={false}
        />
        <div className="chance-photo-vignette" aria-hidden="true" />
        <div className="chance-photo-caption">
          <span>{c.raw}</span>
          <span className="chance-photo-caption__sep">→</span>
          <span>{c.brilliant}</span>
        </div>
      </div>
    </div>
  );
}
